import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { fmtDate, fmtNum } from "@/lib/format";
import { toast } from "sonner";
import {
  Wrench, Gauge, Clock, AlertTriangle, TrendingDown, CheckCircle2, XCircle,
  Plus, Trash2, PlayCircle, StopCircle, Calendar, Cpu, Sparkles, IndianRupee,
  BrainCircuit, TrendingUp, ChevronRight, HelpCircle, Receipt,
} from "lucide-react";
import { useSubscription } from "@/context/SubscriptionContext";

const inr = (n) => `₹${fmtNum(Math.round(Number(n) || 0))}`;
const iso = (d) => d.toISOString();
const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

export default function Maintenance() {
  const { hasModule } = useSubscription();
  const [tab, setTab] = useState("dashboard");
  if (!hasModule("production")) {
    return (
      <div className="p-8"><div className="card-sharp p-10 text-center">
        <div className="font-heading text-2xl">Maintenance requires Enterprise Manufacturing</div>
        <a href="/app/subscription" className="inline-block mt-4 px-5 py-2 bg-accent text-white rounded-sm text-sm">Upgrade →</a>
      </div></div>
    );
  }
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="maintenance-page">
      <PageHeader
        eyebrow="Manufacturing · TPM / CMMS"
        title="Machine Maintenance"
        description="Preventive Maintenance schedules, breakdown & downtime tracking, spare parts, MTBF/MTTR/OEE-Availability KPIs, and cost analysis — plus scheduled windows automatically block the Auto-Scheduler so machines aren't overloaded."
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-sm">
          <TabsTrigger value="dashboard" className="rounded-sm gap-1.5" data-testid="tab-mnt-dashboard"><Gauge className="w-3.5 h-3.5" /> Dashboard</TabsTrigger>
          <TabsTrigger value="pm"        className="rounded-sm gap-1.5" data-testid="tab-mnt-pm"><Calendar className="w-3.5 h-3.5" /> PM Schedules</TabsTrigger>
          <TabsTrigger value="events"    className="rounded-sm gap-1.5" data-testid="tab-mnt-events"><Wrench className="w-3.5 h-3.5" /> Events / Downtime Log</TabsTrigger>
          <TabsTrigger value="analysis"  className="rounded-sm gap-1.5" data-testid="tab-mnt-analysis"><TrendingDown className="w-3.5 h-3.5" /> Reason Analysis</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard" className="mt-6"><DashboardTab /></TabsContent>
        <TabsContent value="pm"        className="mt-6"><PmSchedulesTab /></TabsContent>
        <TabsContent value="events"    className="mt-6"><EventsTab /></TabsContent>
        <TabsContent value="analysis"  className="mt-6"><ReasonAnalysisTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* --------------------------------------------------------------- DASHBOARD */
function DashboardTab() {
  const [from, setFrom] = useState(daysAgo(90));
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const load = async () => {
    try {
      const [k, a] = await Promise.all([
        api.get("/maintenance/kpis", { params: { from_dt: from, to_dt: to } }),
        api.get("/maintenance/predictive-alerts").catch(() => ({ data: { alerts: [], count: 0 } })),
      ]);
      setData(k.data); setAlerts(a.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const totals = data?.totals || {};
  const machines = data?.machines || [];
  const availClr = (p) => p >= 95 ? "text-emerald-600" : p >= 85 ? "text-amber-600" : "text-rose-600";

  return (
    <div className="space-y-4">
      <div className="card-sharp p-4 flex flex-wrap items-end gap-3">
        <div><Label>From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-sm mt-1 w-40" data-testid="mnt-from" /></div>
        <div><Label>To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-sm mt-1 w-40" data-testid="mnt-to" /></div>
        <Button onClick={load} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="mnt-refresh">Refresh</Button>
      </div>

      {alerts && alerts.count > 0 && (
        <div className="card-sharp overflow-hidden border-l-4 border-rose-500" data-testid="predictive-alerts">
          <div className="px-4 py-3 border-b bg-rose-50/60 flex items-center gap-2">
            <BrainCircuit className="w-4 h-4 text-rose-600" />
            <div className="font-heading text-sm text-rose-900">Predictive PM Alerts · {alerts.count} machine{alerts.count > 1 ? "s" : ""} trending down</div>
            <div className="ml-auto text-[10px] text-rose-700 font-mono">Compared vs prior 30-day MTBF</div>
          </div>
          <table className="w-full dense text-sm">
            <thead className="bg-rose-50/30"><tr>
              <th className="text-left px-3 py-2">Machine</th>
              <th className="text-right px-2 py-2">MTBF Curr (h)</th>
              <th className="text-right px-2 py-2">MTBF Prev (h)</th>
              <th className="text-right px-2 py-2">Δ%</th>
              <th className="text-right px-2 py-2">Breakdowns 30d/prev30d</th>
              <th className="text-left px-2 py-2">Severity</th>
              <th className="text-left px-3 py-2">Recommendation</th>
            </tr></thead>
            <tbody>
              {alerts.alerts.map((a) => (
                <tr key={a.machine_id} className="border-b border-border/40 hover:bg-rose-50/20" data-testid={`predalert-${a.machine_code}`}>
                  <td className="px-3 py-1.5">
                    <div className="text-xs font-medium">{a.machine_name}</div>
                    <div className="text-[10px] font-mono text-muted-foreground">{a.machine_code}</div>
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums">{a.current_mtbf_hours}</td>
                  <td className="px-2 py-1 text-right tabular-nums text-muted-foreground">{a.previous_mtbf_hours != null ? a.previous_mtbf_hours : "—"}</td>
                  <td className={`px-2 py-1 text-right tabular-nums font-medium ${a.severity === "high" ? "text-rose-700" : "text-amber-700"}`}>{a.delta_pct}%</td>
                  <td className="px-2 py-1 text-right tabular-nums text-xs">{a.current_breakdowns} / {a.previous_breakdowns}</td>
                  <td className="px-2 py-1"><Badge variant="outline" className={`rounded-sm text-[10px] ${a.severity === "high" ? "border-rose-300 text-rose-700" : "border-amber-300 text-amber-700"}`}>{a.severity}</Badge></td>
                  <td className="px-3 py-1 text-xs">{a.recommendation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {alerts && alerts.count === 0 && (
        <div className="card-sharp p-3 flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50/50" data-testid="predictive-alerts-clean">
          <CheckCircle2 className="w-4 h-4" /> No machines trending down &gt;30% in the last 30 days. Fleet is healthy.
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <KpiTile label="Availability"     value={`${totals.availability_pct || 0}%`} accent={availClr(totals.availability_pct || 100)} icon={<CheckCircle2 className="w-4 h-4" />} />
            <KpiTile label="MTBF (hrs)"       value={fmtNum(totals.mtbf_hours || 0)}    icon={<Clock className="w-4 h-4 text-emerald-500" />} />
            <KpiTile label="MTTR (hrs)"       value={fmtNum(totals.mttr_hours || 0)}    icon={<Clock className="w-4 h-4 text-rose-500" />} />
            <KpiTile label="PM Adherence"     value={totals.pm_adherence_pct != null ? `${totals.pm_adherence_pct}%` : "—"} icon={<Sparkles className="w-4 h-4 text-sky-500" />} />
            <KpiTile label="Downtime (hrs)"   value={fmtNum(totals.downtime_hours || 0)} icon={<TrendingDown className="w-4 h-4 text-amber-500" />} />
            <KpiTile label="Downtime Cost"    value={inr(totals.downtime_cost || 0)}    isText icon={<IndianRupee className="w-4 h-4 text-rose-500" />} />
          </div>

          <div className="card-sharp overflow-hidden">
            <div className="px-4 py-3 border-b bg-muted/20 font-heading text-sm">Per-machine breakdown</div>
            <div className="overflow-x-auto">
              <table className="w-full dense text-sm">
                <thead className="bg-muted/10">
                  <tr>
                    <th className="text-left px-3 py-2">Machine</th>
                    <th className="text-right px-2 py-2">Events</th>
                    <th className="text-right px-2 py-2">Breakdowns</th>
                    <th className="text-right px-2 py-2">PM</th>
                    <th className="text-right px-2 py-2">Planned DT (h)</th>
                    <th className="text-right px-2 py-2">Unplanned DT (h)</th>
                    <th className="text-right px-2 py-2">MTBF (h)</th>
                    <th className="text-right px-2 py-2">MTTR (h)</th>
                    <th className="text-right px-2 py-2">Availability</th>
                    <th className="text-right px-2 py-2">PM Adherence</th>
                    <th className="text-right px-2 py-2">DT Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {machines.length === 0 && <tr><td colSpan={11} className="text-center py-10 text-muted-foreground">No machines / events in window.</td></tr>}
                  {machines.map((m) => (
                    <tr key={m.id} className="border-b border-border/40 hover:bg-muted/20" data-testid={`mnt-mach-${m.code}`}>
                      <td className="px-3 py-1.5">
                        <div className="text-xs font-medium">{m.name}</div>
                        <div className="text-[10px] font-mono text-muted-foreground">{m.code} · MHR ₹{fmtNum(m.mhr_rate)}/hr</div>
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums">{m.events_count}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-rose-600">{m.breakdown_count}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-sky-600">{m.pm_count}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-xs">{fmtNum(m.planned_downtime_hours)}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-xs">{fmtNum(m.unplanned_downtime_hours)}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-xs">{fmtNum(m.mtbf_hours)}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-xs">{fmtNum(m.mttr_hours)}</td>
                      <td className={`px-2 py-1 text-right tabular-nums font-medium ${availClr(m.availability_pct)}`}>{m.availability_pct}%</td>
                      <td className="px-2 py-1 text-right tabular-nums text-xs">{m.pm_adherence_pct != null ? `${m.pm_adherence_pct}%` : "—"}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-xs">{inr(m.downtime_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------- PM SCHEDULES */
function PmSchedulesTab() {
  const [rows, setRows] = useState([]);
  const [machines, setMachines] = useState([]);
  const [form, setForm] = useState({ machine_id: "", task_code: "", task_name: "", frequency_type: "monthly", frequency_value: 1, estimated_hours: 1, checklist: "", is_active: true });
  const [dlg, setDlg] = useState(false);

  const load = async () => {
    try {
      const [r, m] = await Promise.all([
        api.get("/maintenance/pm-schedules"),
        api.get("/mfg/machines"),
      ]);
      setRows(r.data.rows || []);
      setMachines(m.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/maintenance/pm-schedules", {
        ...form,
        frequency_value: Number(form.frequency_value) || 1,
        estimated_hours: Number(form.estimated_hours) || 1,
        checklist: (form.checklist || "").split("\n").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("PM schedule created");
      setDlg(false);
      setForm({ machine_id: "", task_code: "", task_name: "", frequency_type: "monthly", frequency_value: 1, estimated_hours: 1, checklist: "", is_active: true });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const generate = async (pmId) => {
    try {
      const r = await api.post(`/maintenance/pm-schedules/${pmId}/generate-events?horizon_days=90`);
      toast.success(`${r.data.created} PM event(s) generated for the next 90 days`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this PM schedule?")) return;
    try { await api.delete(`/maintenance/pm-schedules/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const toggle = async (row) => {
    try { await api.patch(`/maintenance/pm-schedules/${row.id}`, { is_active: !row.is_active }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          Define recurring maintenance tasks. Click <b>Generate events</b> to materialise the next 90 days of PM slots — they will then show up on the Gantt in MRP → Production Scheduler.
        </div>
        <Dialog open={dlg} onOpenChange={setDlg}>
          <DialogTrigger asChild><Button className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="pm-new"><Plus className="w-4 h-4" /> New PM Schedule</Button></DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle>New PM Schedule</DialogTitle><DialogDescription>Recurring preventive maintenance for a machine.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label>Machine</Label>
                <Select value={form.machine_id} onValueChange={(v) => setForm((f) => ({ ...f, machine_id: v }))}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="pm-machine"><SelectValue placeholder="Pick a machine…" /></SelectTrigger>
                  <SelectContent>{machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.code} · {m.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Task code</Label><Input value={form.task_code} onChange={(e) => setForm((f) => ({ ...f, task_code: e.target.value }))} className="rounded-sm mt-1" placeholder="PM-LUB-01" data-testid="pm-code" /></div>
                <div><Label>Task name</Label><Input value={form.task_name} onChange={(e) => setForm((f) => ({ ...f, task_name: e.target.value }))} className="rounded-sm mt-1" placeholder="Monthly Lubrication" data-testid="pm-name" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Frequency</Label>
                  <Select value={form.frequency_type} onValueChange={(v) => setForm((f) => ({ ...f, frequency_type: v }))}>
                    <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["daily", "weekly", "monthly", "quarterly", "annually", "hours", "cycles"].map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Every</Label><Input type="number" value={form.frequency_value} onChange={(e) => setForm((f) => ({ ...f, frequency_value: e.target.value }))} className="rounded-sm mt-1 tabular-nums" /></div>
                <div><Label>Est. hrs</Label><Input type="number" value={form.estimated_hours} onChange={(e) => setForm((f) => ({ ...f, estimated_hours: e.target.value }))} className="rounded-sm mt-1 tabular-nums" /></div>
              </div>
              <div><Label>Checklist (one per line)</Label><Textarea value={form.checklist} onChange={(e) => setForm((f) => ({ ...f, checklist: e.target.value }))} className="rounded-sm mt-1 min-h-[80px]" placeholder="Inspect belts\nGrease bearings\nCheck coolant level" /></div>
            </div>
            <DialogFooter><Button onClick={save} disabled={!form.machine_id || !form.task_code || !form.task_name} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="pm-save">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-sharp overflow-hidden">
        <table className="w-full dense text-sm">
          <thead className="bg-muted/20"><tr>
            <th className="text-left px-3 py-2">Machine</th>
            <th className="text-left px-3 py-2">Task</th>
            <th className="text-left px-2 py-2">Frequency</th>
            <th className="text-left px-2 py-2">Next Due</th>
            <th className="text-right px-2 py-2">Est. hrs</th>
            <th className="text-left px-2 py-2">Status</th>
            <th className="text-left px-2 py-2">Actions</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">No PM schedules yet. Create one to start tracking preventive maintenance.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border/40" data-testid={`pm-row-${r.task_code}`}>
                <td className="px-3 py-1.5"><div className="text-xs font-medium">{r.machine_name}</div><div className="text-[10px] font-mono text-muted-foreground">{r.machine_code}</div></td>
                <td className="px-3 py-1.5"><div className="text-xs font-medium">{r.task_name}</div><div className="text-[10px] font-mono text-muted-foreground">{r.task_code}</div></td>
                <td className="px-2 py-1 text-xs">every {r.frequency_value} {r.frequency_type}</td>
                <td className="px-2 py-1 text-xs">{fmtDate(r.next_due)}</td>
                <td className="px-2 py-1 text-right tabular-nums text-xs">{r.estimated_hours}</td>
                <td className="px-2 py-1"><button onClick={() => toggle(r)}><Badge variant="outline" className={`rounded-sm text-[10px] ${r.is_active ? "border-emerald-300 text-emerald-700" : "border-slate-300 text-slate-500"}`}>{r.is_active ? "active" : "paused"}</Badge></button></td>
                <td className="px-2 py-1 flex gap-1">
                  <Button onClick={() => generate(r.id)} variant="outline" size="sm" className="rounded-sm text-[10px] h-7 gap-1" data-testid={`pm-gen-${r.task_code}`}><Sparkles className="w-3 h-3" /> Generate 90d</Button>
                  <Button onClick={() => del(r.id)} variant="ghost" size="sm" className="rounded-sm text-rose-600 h-7 w-7 p-0" data-testid={`pm-del-${r.task_code}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- EVENTS TAB */
function EventsTab() {
  const [rows, setRows] = useState([]);
  const [machines, setMachines] = useState([]);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ reason_codes: [], event_types: [] });
  const [filter, setFilter] = useState({ machine_id: "", type: "", status: "" });
  const [dlg, setDlg] = useState(false);
  const [drawer, setDrawer] = useState(null);
  const [form, setForm] = useState(emptyForm());

  function emptyForm() {
    return {
      machine_id: "", type: "Breakdown", reason_code: "MECHANICAL",
      planned_start: iso(new Date()), planned_end: iso(new Date(Date.now() + 3600 * 1000)),
      description: "", root_cause: "", action_taken: "",
      spare_parts: [], labour_cost: 0, opportunity_cost: 0,
      status: "scheduled",
    };
  }

  const load = async () => {
    try {
      const p = {};
      if (filter.machine_id && filter.machine_id !== "all") p.machine_id = filter.machine_id;
      if (filter.type && filter.type !== "all") p.type = filter.type;
      if (filter.status && filter.status !== "all") p.status = filter.status;
      const [r, m, mt, it] = await Promise.all([
        api.get("/maintenance/events", { params: p }),
        api.get("/mfg/machines"),
        api.get("/maintenance/meta"),
        api.get("/items"),
      ]);
      setRows(r.data.rows || []); setMachines(m.data || []); setMeta(mt.data); setItems(it.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.machine_id, filter.type, filter.status]);

  const create = async () => {
    try {
      const payload = { ...form,
        labour_cost: Number(form.labour_cost) || 0,
        opportunity_cost: Number(form.opportunity_cost) || 0,
        spare_parts: (form.spare_parts || []).filter((s) => s.item_id).map((s) => ({
          item_id: s.item_id, qty: Number(s.qty) || 0, unit_cost: Number(s.unit_cost) || 0,
        })),
      };
      await api.post("/maintenance/events", payload);
      toast.success(`${form.type} event logged`);
      setDlg(false); setForm(emptyForm()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const patch = async (ev, upd) => {
    try {
      const r = await api.patch(`/maintenance/events/${ev.id}`, upd);
      // If just completed, decorate the drawer with the freshly-posted GL info so the banner appears without reopening
      if (upd.status === "completed" && r.data?.posted_gl) {
        setDrawer((d) => d && d.id === ev.id ? { ...d, status: "completed", posted_gl: r.data.posted_gl, actual_end: r.data.posted_gl.voucher_date + "T00:00:00Z" } : d);
        toast.success(`Completed · JV ${r.data.posted_gl.voucher_number} posted`);
      } else if (upd.status === "completed" && r.data?.gl_posting_skipped_reason) {
        setDrawer((d) => d && d.id === ev.id ? { ...d, status: "completed", gl_posting_skipped_reason: r.data.gl_posting_skipped_reason } : d);
        toast.warning(`Completed · GL skipped: ${r.data.gl_posting_skipped_reason}`);
      } else {
        toast.success("Updated");
        setDrawer(null);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="card-sharp p-3 flex flex-wrap items-end gap-2">
        <div><Label className="text-[10px]">Machine</Label>
          <Select value={filter.machine_id || "all"} onValueChange={(v) => setFilter((f) => ({ ...f, machine_id: v === "all" ? "" : v }))}>
            <SelectTrigger className="rounded-sm mt-1 w-52"><SelectValue placeholder="All machines" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All machines</SelectItem>{machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.code} · {m.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-[10px]">Type</Label>
          <Select value={filter.type || "all"} onValueChange={(v) => setFilter((f) => ({ ...f, type: v === "all" ? "" : v }))}>
            <SelectTrigger className="rounded-sm mt-1 w-40"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All types</SelectItem>{(meta.event_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-[10px]">Status</Label>
          <Select value={filter.status || "all"} onValueChange={(v) => setFilter((f) => ({ ...f, status: v === "all" ? "" : v }))}>
            <SelectTrigger className="rounded-sm mt-1 w-36"><SelectValue placeholder="All status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All status</SelectItem>{["scheduled", "in_progress", "completed", "cancelled"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex-1" />
        <Dialog open={dlg} onOpenChange={setDlg}>
          <DialogTrigger asChild><Button className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="event-new"><Plus className="w-4 h-4" /> Log Event</Button></DialogTrigger>
          <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Log Maintenance Event</DialogTitle><DialogDescription>Planned PM, unplanned breakdown, setup, changeover, cleaning or inspection.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Machine</Label>
                  <Select value={form.machine_id} onValueChange={(v) => setForm((f) => ({ ...f, machine_id: v }))}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="event-machine"><SelectValue placeholder="Pick machine…" /></SelectTrigger>
                    <SelectContent>{machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.code} · {m.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                    <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta.event_types || ["Breakdown","PM","Setup","Changeover","Cleaning","Inspection"]).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Reason code</Label>
                <Select value={form.reason_code} onValueChange={(v) => setForm((f) => ({ ...f, reason_code: v }))}>
                  <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{(meta.reason_codes || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Planned start</Label><Input type="datetime-local" value={(form.planned_start || "").slice(0, 16)} onChange={(e) => setForm((f) => ({ ...f, planned_start: e.target.value + ":00Z" }))} className="rounded-sm mt-1" /></div>
                <div><Label>Planned end</Label><Input type="datetime-local" value={(form.planned_end || "").slice(0, 16)} onChange={(e) => setForm((f) => ({ ...f, planned_end: e.target.value + ":00Z" }))} className="rounded-sm mt-1" /></div>
              </div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="rounded-sm mt-1" placeholder="Symptom / task description…" /></div>
              {form.type === "Breakdown" && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Root cause</Label><Input value={form.root_cause} onChange={(e) => setForm((f) => ({ ...f, root_cause: e.target.value }))} className="rounded-sm mt-1" data-testid="event-rootcause" /></div>
                    <div><Label>Action taken</Label><Input value={form.action_taken} onChange={(e) => setForm((f) => ({ ...f, action_taken: e.target.value }))} className="rounded-sm mt-1" data-testid="event-action" /></div>
                  </div>
                  <FiveWhyWizard onFill={(rc) => setForm((f) => ({ ...f, root_cause: rc }))} />
                </>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Labour cost (₹)</Label><Input type="number" value={form.labour_cost} onChange={(e) => setForm((f) => ({ ...f, labour_cost: e.target.value }))} className="rounded-sm mt-1 tabular-nums" /></div>
                <div><Label>Opportunity cost (₹)</Label><Input type="number" value={form.opportunity_cost} onChange={(e) => setForm((f) => ({ ...f, opportunity_cost: e.target.value }))} className="rounded-sm mt-1 tabular-nums" /></div>
              </div>
              <SparesEditor form={form} setForm={setForm} items={items} />
            </div>
            <DialogFooter><Button onClick={create} disabled={!form.machine_id} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="event-save">Log Event</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-sharp overflow-hidden">
        <table className="w-full dense text-sm">
          <thead className="bg-muted/20"><tr>
            <th className="text-left px-3 py-2">Machine</th>
            <th className="text-left px-2 py-2">Type</th>
            <th className="text-left px-2 py-2">Reason</th>
            <th className="text-left px-2 py-2">Planned</th>
            <th className="text-left px-2 py-2">Actual</th>
            <th className="text-right px-2 py-2">Hours</th>
            <th className="text-right px-2 py-2">Total Cost</th>
            <th className="text-left px-2 py-2">Status</th>
            <th className="text-left px-2 py-2">Actions</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-muted-foreground">No events logged.</td></tr>}
            {rows.map((r) => {
              const cb = r.cost_breakdown || {};
              const statusClr = r.status === "completed" ? "border-emerald-300 text-emerald-700"
                                : r.status === "in_progress" ? "border-sky-300 text-sky-700"
                                : r.status === "cancelled" ? "border-slate-300 text-slate-500"
                                : "border-amber-300 text-amber-700";
              return (
                <tr key={r.id} className={`border-b border-border/40 hover:bg-muted/20 cursor-pointer ${r.type === "Breakdown" ? "bg-rose-50/30" : ""}`} onClick={() => setDrawer(r)} data-testid={`event-row-${r.id}`}>
                  <td className="px-3 py-1.5"><div className="text-xs font-medium">{r.machine_name}</div><div className="text-[10px] font-mono text-muted-foreground">{r.machine_code}</div></td>
                  <td className="px-2 py-1"><Badge variant="outline" className={`rounded-sm text-[10px] ${r.type === "Breakdown" ? "border-rose-300 text-rose-700" : r.type === "PM" ? "border-sky-300 text-sky-700" : ""}`}>{r.type}</Badge></td>
                  <td className="px-2 py-1 text-[10px] font-mono">{r.reason_code}</td>
                  <td className="px-2 py-1 text-xs">{fmtDate(r.planned_start)}<div className="text-[10px] text-muted-foreground">{fmtDate(r.planned_end)}</div></td>
                  <td className="px-2 py-1 text-xs">{r.actual_start ? fmtDate(r.actual_start) : "—"}<div className="text-[10px] text-muted-foreground">{r.actual_end ? fmtDate(r.actual_end) : "—"}</div></td>
                  <td className="px-2 py-1 text-right tabular-nums text-xs">{cb.hours || 0}</td>
                  <td className="px-2 py-1 text-right tabular-nums text-xs">{inr(cb.total_cost || 0)}</td>
                  <td className="px-2 py-1"><Badge variant="outline" className={`rounded-sm text-[10px] ${statusClr}`}>{r.status}</Badge></td>
                  <td className="px-2 py-1" onClick={(e) => e.stopPropagation()}>
                    {r.status === "scheduled" && <Button onClick={() => patch(r, { status: "in_progress" })} size="sm" variant="outline" className="rounded-sm h-7 text-[10px] gap-1"><PlayCircle className="w-3 h-3" /> Start</Button>}
                    {r.status === "in_progress" && <Button onClick={() => patch(r, { status: "completed" })} size="sm" variant="outline" className="rounded-sm h-7 text-[10px] gap-1 border-emerald-300 text-emerald-700"><StopCircle className="w-3 h-3" /> Complete</Button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Sheet open={!!drawer} onOpenChange={(o) => !o && setDrawer(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {drawer && (<>
            <SheetHeader>
              <SheetTitle>{drawer.type} · {drawer.machine_code}</SheetTitle>
              <SheetDescription>{drawer.description || drawer.reason_code}</SheetDescription>
            </SheetHeader>
            <div className="mt-4 space-y-4">
              <div className="flex gap-2">
                <Badge variant="outline" className="rounded-sm">{drawer.type}</Badge>
                <Badge variant="outline" className="rounded-sm font-mono">{drawer.reason_code}</Badge>
                <Badge variant="outline" className={`rounded-sm ${drawer.status === "completed" ? "border-emerald-300 text-emerald-700" : ""}`}>{drawer.status}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Stat label="Planned start" v={fmtDate(drawer.planned_start)} />
                <Stat label="Planned end"   v={fmtDate(drawer.planned_end)} />
                <Stat label="Actual start"  v={drawer.actual_start ? fmtDate(drawer.actual_start) : "—"} />
                <Stat label="Actual end"    v={drawer.actual_end ? fmtDate(drawer.actual_end) : "—"} />
              </div>
              {drawer.root_cause && <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Root cause</div><div className="text-sm">{drawer.root_cause}</div></div>}
              {drawer.action_taken && <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Action taken</div><div className="text-sm">{drawer.action_taken}</div></div>}
              <div className="border-t pt-3">
                <div className="font-heading text-sm mb-2">Cost breakdown</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <Stat label="Hours" v={drawer.cost_breakdown?.hours} />
                  <Stat label="Machine cost (MHR)" v={inr(drawer.cost_breakdown?.machine_cost)} />
                  <Stat label="Labour" v={inr(drawer.cost_breakdown?.labour_cost)} />
                  <Stat label="Spares" v={inr(drawer.cost_breakdown?.spare_cost)} />
                  <Stat label="Opportunity" v={inr(drawer.cost_breakdown?.opportunity_cost)} />
                  <Stat label="Total" v={inr(drawer.cost_breakdown?.total_cost)} highlight />
                </div>
              </div>
              {(drawer.spare_parts || []).length > 0 && (
                <div className="border-t pt-3">
                  <div className="font-heading text-sm mb-2">Spare parts consumed</div>
                  <table className="w-full dense text-xs">
                    <thead className="bg-muted/20"><tr><th className="text-left px-2">Item</th><th className="text-right px-2">Qty</th><th className="text-right px-2">Unit ₹</th><th className="text-right px-2">Total ₹</th></tr></thead>
                    <tbody>{drawer.spare_parts.map((sp, i) => {
                      const it = items.find((x) => x.id === sp.item_id);
                      return <tr key={i} className="border-t"><td className="px-2 py-1">{it?.name || sp.item_id}</td><td className="px-2 py-1 text-right tabular-nums">{sp.qty}</td><td className="px-2 py-1 text-right tabular-nums">{sp.unit_cost}</td><td className="px-2 py-1 text-right tabular-nums">{fmtNum(sp.qty * sp.unit_cost)}</td></tr>;
                    })}</tbody>
                  </table>
                </div>
              )}
              {drawer.posted_gl && (
                <div className="border-t pt-3" data-testid="event-gl-posted">
                  <div className="font-heading text-sm mb-2 flex items-center gap-2"><Receipt className="w-4 h-4 text-emerald-600" /> Auto-posted to Ledger</div>
                  <div className="rounded-sm border border-emerald-300 bg-emerald-50/60 p-3 space-y-1.5 text-xs">
                    <div>Journal Voucher: <b className="font-mono">{drawer.posted_gl.voucher_number}</b> · {fmtDate(drawer.posted_gl.voucher_date)}</div>
                    <div>Dr Repairs &amp; Maintenance ₹{fmtNum(drawer.posted_gl.total_expense)}
                      {drawer.posted_gl.spare_cost > 0 && <> · Cr Inventory (Spares) ₹{fmtNum(drawer.posted_gl.spare_cost)}</>}
                      {drawer.posted_gl.labour_cost > 0 && <> · Cr Sundry Creditors ₹{fmtNum(drawer.posted_gl.labour_cost)}</>}
                    </div>
                    {(drawer.posted_gl.movements || []).length > 0 && (
                      <div className="text-muted-foreground text-[10px]">
                        {drawer.posted_gl.movements.length} outward stock movement{drawer.posted_gl.movements.length > 1 ? "s" : ""} · Total value ₹{fmtNum(drawer.posted_gl.movements.reduce((a, m) => a + m.value, 0))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {drawer.gl_posting_skipped_reason && (
                <div className="border-t pt-3">
                  <div className="rounded-sm border border-amber-300 bg-amber-50/60 p-3 text-xs text-amber-900 flex gap-2" data-testid="event-gl-skipped">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div>GL posting skipped: <b>{drawer.gl_posting_skipped_reason}</b>. Add the missing ledger under Chart of Accounts, then reopen + re-complete this event to post it.</div>
                  </div>
                </div>
              )}
              <div className="border-t pt-3 flex gap-2">
                {drawer.status !== "completed" && <Button onClick={() => patch(drawer, { status: "completed" })} className="flex-1 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white gap-1"><StopCircle className="w-4 h-4" /> Mark Completed</Button>}
                {drawer.status === "scheduled" && <Button onClick={() => patch(drawer, { status: "cancelled" })} variant="outline" className="rounded-sm gap-1"><XCircle className="w-4 h-4" /> Cancel</Button>}
              </div>
            </div>
          </>)}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function SparesEditor({ form, setForm, items }) {
  const add = () => setForm((f) => ({ ...f, spare_parts: [...(f.spare_parts || []), { item_id: "", qty: 1, unit_cost: 0 }] }));
  const upd = (i, k, v) => setForm((f) => {
    const sp = [...(f.spare_parts || [])]; sp[i] = { ...sp[i], [k]: v }; return { ...f, spare_parts: sp };
  });
  const del = (i) => setForm((f) => ({ ...f, spare_parts: (f.spare_parts || []).filter((_, x) => x !== i) }));
  const spares = items.filter((x) => (x.item_type || "").includes("spare") || (x.category || "").toLowerCase().includes("spare") || x.item_type === "goods");
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label>Spare parts</Label>
        <Button onClick={add} type="button" variant="outline" size="sm" className="rounded-sm h-7 text-[10px] gap-1"><Plus className="w-3 h-3" /> Add spare</Button>
      </div>
      {(form.spare_parts || []).length > 0 && (
        <div className="space-y-1 mt-2">
          {(form.spare_parts || []).map((sp, i) => (
            <div key={i} className="grid grid-cols-[1fr_80px_100px_28px] gap-2 items-center">
              <Select value={sp.item_id} onValueChange={(v) => upd(i, "item_id", v)}>
                <SelectTrigger className="rounded-sm h-8 text-xs"><SelectValue placeholder="Pick item…" /></SelectTrigger>
                <SelectContent>{spares.map((it) => <SelectItem key={it.id} value={it.id}>{it.sku} · {it.name}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" value={sp.qty} onChange={(e) => upd(i, "qty", e.target.value)} className="rounded-sm h-8 text-xs tabular-nums" placeholder="qty" />
              <Input type="number" value={sp.unit_cost} onChange={(e) => upd(i, "unit_cost", e.target.value)} className="rounded-sm h-8 text-xs tabular-nums" placeholder="₹/unit" />
              <button onClick={() => del(i)} className="text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------- 5-Why WIZARD */
function FiveWhyWizard({ onFill }) {
  const [open, setOpen] = useState(false);
  const [whys, setWhys] = useState(["", "", "", "", ""]);
  const [step, setStep] = useState(0);
  const prompts = [
    "Why did the machine fail / stop?",
    "Why did that happen?",
    "Why did that underlying condition arise?",
    "Why wasn't it caught earlier?",
    "Why is the root cause? (systemic — process, training, design)",
  ];
  const commit = () => {
    const filled = whys.map((w, i) => w.trim() ? `Why ${i + 1}: ${w.trim()}` : null).filter(Boolean).join(" · ");
    if (!filled) { toast.warning("Fill at least the first Why"); return; }
    onFill(filled);
    toast.success("Root cause built from 5-Why chain");
    setOpen(false);
    setWhys(["", "", "", "", ""]); setStep(0);
  };
  if (!open) return (
    <div>
      <Button type="button" variant="outline" onClick={() => setOpen(true)} className="rounded-sm gap-1.5 text-xs h-8" data-testid="whywizard-open">
        <BrainCircuit className="w-3.5 h-3.5 text-sky-600" /> 5-Why Root-Cause Wizard
      </Button>
      <div className="text-[10px] text-muted-foreground mt-1">Turn "one-word root cause" habit into a proper Toyota-style 5-Why analysis. Optional but recommended for every breakdown.</div>
    </div>
  );
  return (
    <div className="rounded-sm border border-sky-200 bg-sky-50/40 p-3 space-y-2" data-testid="whywizard-panel">
      <div className="flex items-center gap-2">
        <BrainCircuit className="w-4 h-4 text-sky-600" />
        <div className="font-heading text-xs">5-Why Analysis · Step {step + 1} of 5</div>
        <div className="ml-auto text-[10px] text-muted-foreground">Ask "Why?" until you reach a systemic cause you can fix.</div>
      </div>
      <div className="space-y-2">
        {whys.map((w, i) => {
          const active = i === step;
          if (i > step) return null;
          return (
            <div key={i} className={`${active ? "" : "opacity-70"}`}>
              <Label className="text-[10px] font-mono">{prompts[i]}</Label>
              <Input
                autoFocus={active}
                value={w}
                onChange={(e) => { const n = [...whys]; n[i] = e.target.value; setWhys(n); }}
                className="rounded-sm mt-1 text-xs"
                placeholder={`Why ${i + 1} · ${active ? "type answer, press Next" : ""}`}
                data-testid={`whywizard-why-${i + 1}`}
              />
            </div>
          );
        })}
      </div>
      <div className="flex gap-2 items-center pt-1">
        {step > 0 && <Button type="button" variant="ghost" size="sm" onClick={() => setStep(step - 1)} className="rounded-sm text-xs h-7">← Back</Button>}
        {step < 4 && <Button type="button" size="sm" onClick={() => setStep(step + 1)} disabled={!whys[step].trim()} className="rounded-sm bg-sky-600 hover:bg-sky-700 text-white text-xs h-7 gap-1" data-testid="whywizard-next">Next Why <ChevronRight className="w-3 h-3" /></Button>}
        {step >= 4 && whys[4].trim() && <Button type="button" size="sm" onClick={commit} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7 gap-1" data-testid="whywizard-commit"><CheckCircle2 className="w-3 h-3" /> Fill Root Cause</Button>}
        {step >= 1 && <Button type="button" size="sm" variant="outline" onClick={commit} className="rounded-sm text-xs h-7" data-testid="whywizard-commit-early">Use partial chain</Button>}
        <div className="flex-1" />
        <Button type="button" size="sm" variant="ghost" onClick={() => { setOpen(false); setWhys(["", "", "", "", ""]); setStep(0); }} className="rounded-sm text-xs h-7 text-muted-foreground">Cancel</Button>
      </div>
    </div>
  );
}

/* --------------------------------------------------------- REASON ANALYSIS */
function ReasonAnalysisTab() {
  const [from, setFrom] = useState(daysAgo(90));
  const [to, setTo] = useState(today());
  const [rows, setRows] = useState([]);
  const load = async () => {
    try {
      const r = await api.get("/maintenance/reason-analysis", { params: { from_dt: from, to_dt: to } });
      setRows(r.data.rows || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const totalHrs = rows.reduce((a, r) => a + r.hours, 0) || 1;
  const totalCost = rows.reduce((a, r) => a + r.cost, 0);

  return (
    <div className="space-y-4">
      <div className="card-sharp p-4 flex flex-wrap items-end gap-3">
        <div><Label>From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-sm mt-1 w-40" /></div>
        <div><Label>To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-sm mt-1 w-40" /></div>
        <Button onClick={load} className="rounded-sm bg-accent hover:bg-accent/90 text-white">Refresh</Button>
        <div className="flex-1" />
        <div className="text-right">
          <div className="text-[10px] font-mono uppercase text-muted-foreground">Total downtime</div>
          <div className="text-2xl font-heading text-rose-700">{fmtNum(totalHrs)} hrs · {inr(totalCost)}</div>
        </div>
      </div>

      <div className="card-sharp overflow-hidden">
        <table className="w-full dense text-sm">
          <thead className="bg-muted/20"><tr>
            <th className="text-left px-3 py-2">Reason Code</th>
            <th className="text-right px-2 py-2">Events</th>
            <th className="text-right px-2 py-2">Hours</th>
            <th className="text-right px-2 py-2">Cost</th>
            <th className="text-left px-3 py-2 w-1/3">Share of downtime</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-muted-foreground">No completed events in window.</td></tr>}
            {rows.map((r) => {
              const pct = totalHrs > 0 ? (r.hours / totalHrs) * 100 : 0;
              return (
                <tr key={r.reason_code} className="border-b border-border/40" data-testid={`reason-${r.reason_code}`}>
                  <td className="px-3 py-1.5 font-mono text-xs">{r.reason_code}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{r.events}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{r.hours}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{inr(r.cost)}</td>
                  <td className="px-3 py-1">
                    <div className="w-full bg-muted/20 rounded-sm h-4 overflow-hidden">
                      <div className="h-4 bg-rose-500/70" style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                    <div className="text-[10px] mt-0.5 tabular-nums">{pct.toFixed(1)}%</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- tiny helpers */
function KpiTile({ label, value, accent, icon, isText }) {
  return (
    <div className="card-sharp p-3">
      <div className="text-[10px] font-mono uppercase text-muted-foreground flex items-center gap-1">{icon} {label}</div>
      <div className={`text-2xl font-heading mt-1 ${accent || ""}`}>{isText ? value : value}</div>
    </div>
  );
}
function Stat({ label, v, highlight }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase text-muted-foreground">{label}</div>
      <div className={`text-sm tabular-nums ${highlight ? "font-heading text-rose-700" : ""}`}>{v ?? "—"}</div>
    </div>
  );
}
